import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import { queryKeys } from '../lib/queryKeys';
import type {
  CreateArticleCollectionRequest,
  ReorderArticleCollectionsRequest,
  UpdateArticleCollectionRequest,
} from '../types/articleCollections';

/** The organization's knowledge base collections in display order (each followed by its sections) */
export function useArticleCollections(organizationId?: string) {
  return useQuery({
    queryKey: queryKeys.articleCollections.list(organizationId),
    queryFn: async () => {
      const response = await apiService.getArticleCollections(organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to fetch collections'));
      }
      return response.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!organizationId,
  });
}

// Collection changes show up on articles (their collection name) and on categories (their suggested collection)
function invalidateAfterCollectionChange(queryClient: QueryClient, organizationId?: string, articlesToo = false) {
  queryClient.invalidateQueries({ queryKey: queryKeys.articleCollections.list(organizationId) });
  queryClient.invalidateQueries({ queryKey: queryKeys.categories.list(organizationId) });
  if (articlesToo) {
    queryClient.invalidateQueries({ queryKey: queryKeys.articles.all });
  }
}

export function useCreateArticleCollection(organizationId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateArticleCollectionRequest) => {
      const response = await apiService.createArticleCollection(request, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to create collection'));
      }
      return response.data;
    },
    onSuccess: () => invalidateAfterCollectionChange(queryClient, organizationId),
  });
}

export function useUpdateArticleCollection(organizationId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateArticleCollectionRequest }) => {
      const response = await apiService.updateArticleCollection(id, updates, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to update collection'));
      }
      return response.data;
    },
    onSuccess: () => invalidateAfterCollectionChange(queryClient, organizationId, true),
  });
}

export function useReorderArticleCollections(organizationId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: ReorderArticleCollectionsRequest) => {
      const response = await apiService.reorderArticleCollections(request, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to reorder collections'));
      }
      return response.data;
    },
    onSettled: () => invalidateAfterCollectionChange(queryClient, organizationId),
  });
}

/** Deletes a collection, moving its articles to moveArticlesToCollectionId (or leaving them uncategorized) */
export function useDeleteArticleCollection(organizationId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, moveArticlesToCollectionId }: { id: string; moveArticlesToCollectionId: string | null }) => {
      const response = await apiService.deleteArticleCollection(id, moveArticlesToCollectionId, organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to delete collection'));
      }
      return response.data;
    },
    onSuccess: () => invalidateAfterCollectionChange(queryClient, organizationId, true),
  });
}
